import React, { useMemo, useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import classes from "./TotalBox.module.css"

import {
  useBooking,
  getPaidEquipmentPreisRowLabel,
  formatEquipmentPreisEuros,
  roundEuros,
} from "../../store/bookingStore";
import { getSelectedPackages } from "../../util/getSelectedPackages";
import { calculateCreativePackagesPrice } from "../../util/calculateCreativePackagesPrice";
import { getWeekendSurchargeLineLabel } from "../../util/getWeekendSurchargeLineLabel";
import { getDiscountLineLabel } from "../../util/getDiscountLineLabel";
import { buildStudioCoverImageCandidates } from "../../util/studioImageUtils";
import { parseLocalDateString } from "../../util/parseLocalDate";

const TotalBox = ({style, studio, studioCreativesData}) => {
  const { bookingData } = useBooking();

  const hourlyRate = studio?.pricePerHour;

  // Detect if this studio uses package-based pricing
  // IMPORTANT:
  // - Only treat the studio as "package pricing" when pricingMode is explicitly "PACKAGES"
  // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
  const isPackagePricing = useMemo(
    () => studio?.pricingMode === 'PACKAGES',
    [studio?.pricingMode]
  );

  const date = bookingData?.date ? format(parseLocalDateString(bookingData.date), "EEEE, d. MMMM, yyyy", {
    locale: de
  }) : '-';

  const calculatreEndTime = () => {
    if (bookingData?.endTime === "00:00") {
      return "24:00";
    }
    return bookingData?.endTime;
  }
  
  const startTimeNum = parseInt(bookingData?.startTime);
  const endTimeNum = parseInt(calculatreEndTime());
  const totalHours = endTimeNum - startTimeNum;

  // Duration is either the explicit package duration or the booked hours
  const durationInHours = isPackagePricing
    ? (bookingData?.packageDurationHours || totalHours || 0)
    : (totalHours || 0);

  // Base studio price: always read from the authoritative booking store.
  // useBookingPricingSync writes baseStudioPrice (raw base, before surcharge/discount)
  // via calculateStudioPrice, which range-matches the guest count exactly as the Lambda does.
  // Previously this useMemo recomputed locally with an exact-key + prices[0] fallback;
  // that could show a cheaper tier than the authoritative total on multi-package studios
  // with heterogeneous people groups. Now both paths read the same store value.
  const baseStudioPrice = useMemo(() => {
    if (isPackagePricing) {
      return Number(bookingData?.baseStudioPrice) || 0;
    }
    // Hourly mode: prefer centrally calculated baseStudioPrice from booking store
    if (bookingData?.baseStudioPrice != null) {
      return Number(bookingData.baseStudioPrice) || 0;
    }
    // Fallback: simple hourlyRate * totalHours when no store value yet
    if (!hourlyRate || !totalHours || Number.isNaN(totalHours)) return 0;
    return hourlyRate * totalHours;
  }, [
    isPackagePricing,
    bookingData?.baseStudioPrice,
    hourlyRate,
    totalHours,
  ]);

  // Get selected creative packages from studioCreativesData
  const selectedPackages = useMemo(() => {
    if (!studioCreativesData || !bookingData?.creativePackages || bookingData.creativePackages.length === 0) {
      return [];
    }
    return getSelectedPackages(studioCreativesData, bookingData.creativePackages);
  }, [studioCreativesData, bookingData?.creativePackages]);

  // Gesamtbetrag: immer direkt aus dem Booking-Store nehmen.
  // Dort wird der vollständige Gesamtpreis zentral berechnet
  // (Studio + Gebühren + Creative + Equipment).
  const displayPrice = useMemo(
    () => roundEuros(bookingData.totalPrice != null ? Number(bookingData.totalPrice) : 0),
    [bookingData.totalPrice]
  );
  const hasUnavailablePricing = Boolean(bookingData?.pricingUnavailable);

  const campaignDiscountAmount = useMemo(() => {
    if (!bookingData?.serverPriceSynced || !bookingData?.campaignApplied) return 0;
    const cents = Number(bookingData?.campaignDiscountAmountCents);
    if (!Number.isFinite(cents) || cents <= 0) return 0;
    return roundEuros(cents / 100);
  }, [
    bookingData?.serverPriceSynced,
    bookingData?.campaignApplied,
    bookingData?.campaignDiscountAmountCents,
  ]);

  const studioHourly = useMemo(() => {
    if (isPackagePricing) return null;
    if (durationInHours && bookingData.baseStudioPrice) {
      return Math.round((bookingData.baseStudioPrice / durationInHours) * 100) / 100;
    }
    // Fallback to flat hourlyRate only when we don't yet have a calculated baseStudioPrice
    return hourlyRate;
  }, [isPackagePricing, durationInHours, bookingData.baseStudioPrice, hourlyRate]);

  const peopleLabel = useMemo(() => {
    if (bookingData?.selectedPeopleGroupKey) {
      const [min, max] = bookingData.selectedPeopleGroupKey.split('-');
      if (min && max) return `${min}-${max} Personen`;
    }
    if (bookingData?.numberOfPeople) {
      const value = String(bookingData.numberOfPeople);
      return value.includes('Person') ? value : `${value} Personen`;
    }
    return '1-5 Personen';
  }, [bookingData?.selectedPeopleGroupKey, bookingData?.numberOfPeople]);

  const numberOfReviews = 20;
  const studioCoverImageCandidates = useMemo(
    () => buildStudioCoverImageCandidates(studio),
    [studio?.id, studio?.originalImages, studio?.processedImages]
  );
  const [coverCandidateIndex, setCoverCandidateIndex] = useState(0);
  const coverImageErrorExhaustedRef = useRef(false);

  useEffect(() => {
    setCoverCandidateIndex(0);
    coverImageErrorExhaustedRef.current = false;
  }, [studio?.id, studioCoverImageCandidates]);

  const studioImageSrc = studioCoverImageCandidates[coverCandidateIndex] ?? '';

  const handleStudioImageError = () => {
    if (coverImageErrorExhaustedRef.current) return;
    setCoverCandidateIndex((i) => {
      if (i + 1 >= studioCoverImageCandidates.length) {
        coverImageErrorExhaustedRef.current = true;
        return i;
      }
      return i + 1;
    });
  };

  return (
    <div className={classes.totalBox} style={style}>
      <div className={classes.studioProfile}>
        <div className={classes.studioProfileImage}>
          <img
            src={studioImageSrc}
            alt="Studio"
            onError={handleStudioImageError}
          />
        </div>
        <div className={classes.studioProfileText}>
          <div className={classes.studioProfileTextFirst}>
            <h3 className="mediumLarge">{studio?.StudioName}</h3>
            <p className="slimRegularSmallText">{studio?.city}</p>
            <p className="slimRegularSmallText">{studio?.country}</p>
          </div>
          <div className={classes.studioProfileTextSecond}>
            {studio?.rating && (
              <>
                <p>{studio?.rating}
                  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                    <g clipPath="url(#clip0_4470_15656)">
                  <path d="M6.42769 5.84833L6.74849 7.7286L4.3291 6.46321L6.28957 5.05078L6.38313 5.58991L6.42769 5.84833Z" fill="#1D1E1B"/>
                  <path d="M2.27553 5.58964L2.36909 5.05051L0.413086 3.15688L3.11763 2.76479L4.32956 0.332031L5.54148 2.76479L8.24603 3.15688L1.91017 7.72832L2.27553 5.58964Z" fill="#1D1E1B"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_4470_15656">
                      <rect width="7.83294" height="7.39629" fill="white" transform="translate(0.413086 0.332031)"/>
                    </clipPath>
                  </defs>
                </svg>
                </p>
                <span className="slimRegularSmallText">({numberOfReviews})</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={classes.horizontalLine}></div>
      <h2 className={`${classes.deineBuchung} titleSemibold3`}>Deine Buchung</h2>
      <div className={classes.bookingDetails}>
        <h3 className='semiboldSmall'>Datum und Zeit</h3>
        <div className={classes.bookingDetailsText}>
          <p >{date}</p>
          {totalHours > 0 && (
            <p>{bookingData?.startTime} - {bookingData?.endTime} Uhr</p>
          )}
        </div>
        <h3 className='semiboldSmall'>Teilnehmer</h3>
        <p>{peopleLabel}</p>
      </div>
      {(baseStudioPrice > 0 || bookingData?.pricingUnavailable) && (
        <div className={classes.horizontalLine}></div>
      )}
      {(baseStudioPrice > 0 || bookingData?.pricingUnavailable) && (
        <div className={`${classes.priceBreakdown} regularLarge`}>
              <h3 className='titleSemibold3'>Preis</h3>
              <div className={classes.priceItem}>
                <span>
                  {isPackagePricing
                    ? 'Paketpreis'
                    : `${studioHourly} € x ${durationInHours} Stunden`}
                </span>
                <span style={{whiteSpace: 'nowrap'}}>
                  {bookingData?.pricingUnavailable
                    ? 'Nicht verfügbar'
                    : `${baseStudioPrice} €`}
                </span>
              </div>
          {hasUnavailablePricing && (
            <div className={classes.priceItem}>
              <span>{bookingData.pricingUnavailableReason || 'Preis für diese Personengruppe nicht verfügbar.'}</span>
            </div>
          )}
          {/* Show discount if applied */}
          {!hasUnavailablePricing && bookingData?.appliedDiscount > 0 && (
            <div className={classes.priceItem}>
              <span>{getDiscountLineLabel(bookingData)}</span>
              <span style={{whiteSpace: 'nowrap'}}>-{bookingData.appliedDiscount} €</span>
            </div>
          )}
          {!hasUnavailablePricing && campaignDiscountAmount > 0 && (
            <div className={classes.priceItem}>
              <span>Kampagnenrabatt</span>
              <span style={{whiteSpace: 'nowrap'}}>-{formatEquipmentPreisEuros(campaignDiscountAmount)}</span>
            </div>
          )}
          {/* Show weekend surcharge if applied */}
          {!hasUnavailablePricing && bookingData?.appliedSurcharge > 0 && (
            <div className={classes.priceItem}>
              <span>{getWeekendSurchargeLineLabel(bookingData)}</span>
              <span style={{whiteSpace: 'nowrap'}}>+{formatEquipmentPreisEuros(bookingData.appliedSurcharge)}</span>
            </div>
          )}
          {/* Show fees if applied */}
          {!hasUnavailablePricing && bookingData?.appliedFees && bookingData.appliedFees.length > 0 && (
            bookingData.appliedFees.map((fee) => (
              <div className={classes.priceItem} key={fee.id}>
                <span>{fee.description || 'Gebühr'}</span>
                <span style={{whiteSpace: 'nowrap'}}>+{fee.amount} €</span>
              </div>
            ))
          )}
          {!hasUnavailablePricing && calculateCreativePackagesPrice(
            selectedPackages,
            isPackagePricing
              ? (Number(bookingData?.packageDurationHours) || durationInHours)
              : Math.max(durationInHours, Number(studio?.minNumberOfHours) || 1)
          ).lines.map((line) => (
            <div className={classes.priceItem} key={line.id}>
              <span>
                {line.name}:{' '}
                {line.isHourly
                  ? `${line.unitPrice} € x ${line.hours} Stunden`
                  : 'Pauschale'}
              </span>
              <span style={{whiteSpace: 'nowrap'}}>+{formatEquipmentPreisEuros(line.lineTotal)}</span>
            </div>
          ))}
          {!hasUnavailablePricing && bookingData?.equipment?.filter((item) => (Number(item.totalPrice) || 0) > 0).map((item, index) => (
            <div className={classes.priceItem} key={`${item.name}-${index}`}>
              <span>{getPaidEquipmentPreisRowLabel(item)}</span>
              <span style={{ whiteSpace: "nowrap" }}>
                +{formatEquipmentPreisEuros(item.totalPrice)}
              </span>
            </div>
          ))}
          <div className={classes.horizontalLine}></div>
        </div>
      )}
      {durationInHours > 0 && (
          <div className={classes.totalAmount}>
          <h3>Gesamtbetrag</h3>
          <span>{hasUnavailablePricing ? 'Nicht verfügbar' : formatEquipmentPreisEuros(displayPrice)}</span>
        </div>
      )}
    </div>
  );
};

export default TotalBox;
